import {
  DimensionType,
  DimensionTypeByAggBiz,
  DimensionTypeByBaseBiz,
  DimensionTypes,
  DimensionTypesByAggBiz,
  DimensionTypesByBaseBiz,
  MetricType,
  MetricTypeByAggBiz,
  MetricTypeByBaseBiz,
  MetricTypes,
  MetricTypesByAggbiz,
  MetricTypesByBaseBiz,
} from '../entities/Dashboard';

/**
 * ダッシュボードのサービス
 */
export class DashboardService {
  /**
   * あるstringがDimensionTypeであるかを判定する
   * @param value - stringの判定したい値
   */
  public static isDimensionType(value: string): value is DimensionType {
    return Object.values(DimensionTypes).includes(value as DimensionType);
  }

  /**
   * あるstringがDimensionTypeByAggBizであるかを判定する
   * @param value - stringの判定したい値
   */
  public static isDimensionTypeByAggBiz(value: DimensionType): value is DimensionTypeByAggBiz {
    return Object.values(DimensionTypesByAggBiz).includes(value as DimensionTypeByAggBiz);
  }

  /**
   * あるstringがMetricTypeByAggBizであるかを判定する
   * @param value - stringの判定したい値
   */
  public static isDimensionTypeByBaseBiz(value: DimensionType): value is DimensionTypeByBaseBiz {
    return Object.values(DimensionTypesByBaseBiz).includes(value as DimensionTypeByBaseBiz);
  }

  /**
   * ある2つのMetricValueが同じ取引種類のMetricTypeかどうかを判定する
   * @param a - 判定したいMetricType1
   * @param b - 判定したいMetricType2
   */
  public static isSameDimensionType(a: DimensionType, b: DimensionType): boolean {
    if (this.isUnknown(a) || this.isUnknown(b)) return false;
    return this.isDimensionTypeByAggBiz(a) === this.isDimensionTypeByAggBiz(b);
  }

  /**
   * あるstringがMetricTypeであるかを判定する
   * @param value - stringの判定したい値
   */
  public static isMetricType(value: string): value is MetricType {
    return Object.values(MetricTypes).includes(value as MetricType);
  }

  /**
   * あるstringがMetricTypeByAggBizであるかを判定する
   * @param value - stringの判定したい値
   */
  public static isMetricTypeByAggBiz(value: MetricType): value is MetricTypeByAggBiz {
    return Object.values(MetricTypesByAggbiz).includes(value as MetricTypeByAggBiz);
  }

  /**
   * あるstringがMetricTypeByAggBizであるかを判定する
   * @param value - stringの判定したい値
   */
  public static isMetricTypeByBaseBiz(value: MetricType): value is MetricTypeByBaseBiz {
    return Object.values(MetricTypesByBaseBiz).includes(value as MetricTypeByBaseBiz);
  }

  /**
   * ある2つのMetricValueが同じ取引種類のMetricTypeかどうかを判定する
   * @param a - 判定したいMetricType1
   * @param b - 判定したいMetricType2
   */
  public static isSameMetricType(a: MetricType, b: MetricType): boolean {
    if (this.isUnknown(a) || this.isUnknown(b)) return false;
    return this.isMetricTypeByAggBiz(a) === this.isMetricTypeByAggBiz(b);
  }

  /**
   * ある2つのMetricValueが同じ取引種類のMetricTypeかどうかを判定する
   * @param a - 判定したいMetricType1
   * @param b - 判定したいMetricType2
   */
  public static isMetricTypesSameBusinessType(
    a: MetricType,
    b: MetricType
  ): { result: boolean; type: 'aggregateBusiness' | 'baseBusiness' | undefined } {
    if (this.isUnknown(a) || this.isUnknown(b)) return { result: false, type: undefined };
    const result = this.isMetricTypeByAggBiz(a) === this.isMetricTypeByAggBiz(b);
    return { result, type: result ? (this.isMetricTypeByAggBiz(a) ? 'aggregateBusiness' : 'baseBusiness') : undefined };
  }

  /**
   * 値がUnknown種別かどうかを判定する。
   * @param value - 判定対象
   * @returns Unknownであれば`true`
   */
  public static isUnknown(value: DimensionType | MetricType): boolean {
    return value === DimensionTypes.UNKNOWN || value === MetricTypes.UNKNOWN;
  }

  /**
   * 指標種別と切り口種別の組み合わせが有効か判定する。
   * @param metricType - 指標種別
   * @param dimensionType - 切り口種別
   * @returns 組み合わせが有効であれば`true`
   */
  public static isMetricTypeAndDimensionTypeIsValid(metricType: MetricType, dimensionType: DimensionType): boolean {
    return !this.isUnknown(metricType) && !this.isUnknown(dimensionType);
  }
}
